import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription } from '../../ui/alert';
import {
  Calendar,
  FileSpreadsheet,
  ChevronDown,
  ChevronUp,
  Clock,
  Users,
  CheckCircle,
  XCircle,
  AlertCircle,
  Briefcase
} from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'https://munca-la-schimb-production.up.railway.app/api';

interface HRMonthlyReportTabProps {
  isLoading?: boolean;
}

interface WorkEntryDetail {
  date: string;
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  hoursWorked: number;
  hoursWorkedFormatted: string;
  effectiveHours: number;
  effectiveHoursFormatted: string;
  expectedHoursPerDay: number;
  expectedHoursPerDayFormatted: string;
  breakMinutes: number;
  projectName: string;
  locationName: string;
  comments: string;
  isAutoClosed: boolean;
}

interface OvertimeDetail {
  date: string;
  overtimeHours: number;
  overtimeHoursFormatted: string;
  reason: string;
  isApproved: boolean;
  isDenied: boolean;
  isAutoGenerated: boolean;
  approvedByName: string;
  approvedAt: string;
  approvalComment: string;
}

interface WorkerMonthlyReport {
  userId: number;
  userName: string;
  userRole: string;
  workerType: string;
  teamName: string;
  dailyWorkQuotaHours: number;
  dailyBreakMinutes: number;
  daysWorked: number;
  totalHoursWorked: number;
  totalHoursWorkedFormatted: string;
  totalBreakHours: number;
  totalBreakHoursFormatted: string;
  totalEffectiveHours: number;
  totalEffectiveHoursFormatted: string;
  expectedHours: number;
  expectedHoursFormatted: string;
  metMonthlyQuota: boolean;
  autoCalculatedOvertimeHours: number;
  autoCalculatedOvertimeHoursFormatted: string;
  approvedOvertimeHours: number;
  pendingOvertimeHours: number;
  rejectedOvertimeHours: number;
  saturdayDaysWorked: number;
  saturdayTotalHoursWorked: number;
  saturdayTotalHoursWorkedFormatted: string;
  saturdayWorkEntries: WorkEntryDetail[];
  firstWorkDate: string;
  lastWorkDate: string;
  workEntries: WorkEntryDetail[];
  overtimeEntries: OvertimeDetail[];
}

interface MonthlyHRReport {
  year: number;
  month: number;
  monthName: string;
  startDate: string;
  endDate: string;
  totalWorkers: number;
  totalDaysInMonth: number;
  workerReports: WorkerMonthlyReport[];
  generatedAt: string;
}

export const HRMonthlyReportTab: React.FC<HRMonthlyReportTabProps> = ({ isLoading: parentLoading }) => {
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [report, setReport] = useState<MonthlyHRReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedWorkers, setExpandedWorkers] = useState<Set<number>>(new Set());

  const months = [
    { value: 1, label: 'Ianuarie' }, { value: 2, label: 'Februarie' },
    { value: 3, label: 'Martie' }, { value: 4, label: 'Aprilie' },
    { value: 5, label: 'Mai' }, { value: 6, label: 'Iunie' },
    { value: 7, label: 'Iulie' }, { value: 8, label: 'August' },
    { value: 9, label: 'Septembrie' }, { value: 10, label: 'Octombrie' },
    { value: 11, label: 'Noiembrie' }, { value: 12, label: 'Decembrie' }
  ];

  const years = Array.from({ length: 10 }, (_, i) => currentDate.getFullYear() - 5 + i);

  const handleGenerateReport = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_URL}/hrreport/monthly?year=${selectedYear}&month=${selectedMonth}`
      );
      if (!response.ok) throw new Error('Nu s-a putut genera raportul');
      const data: MonthlyHRReport = await response.json();
      setReport(data);
    } catch (err: any) {
      setError(err.message);
      setReport(null);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleWorkerExpansion = (userId: number) => {
    const newExpanded = new Set(expandedWorkers);
    if (newExpanded.has(userId)) newExpanded.delete(userId);
    else newExpanded.add(userId);
    setExpandedWorkers(newExpanded);
  };

  const handleExportToExcel = () => {
    if (!report) return;

    const wb = XLSX.utils.book_new();

    // Summary sheet (weekday only)
    const summaryData = report.workerReports.map(worker => ({
      'Nume Muncitor': worker.userName,
      'Tip Muncitor': worker.workerType,
      'Echipa': worker.teamName,
      'Zile Lucrate': worker.daysWorked,
      'Total Ore Lucrate Efectiv': worker.totalHoursWorkedFormatted,
      'Pauza Masa': worker.totalBreakHoursFormatted,
      'Ore Lucrate Fara Pauza': worker.totalEffectiveHoursFormatted,
      'Ore Lucratoare/Luna': worker.expectedHoursFormatted,
      'Norma Completata': worker.metMonthlyQuota ? 'DA' : 'NU',
      'Ore Suplimentare In Asteptare': worker.autoCalculatedOvertimeHoursFormatted,
      'Ore Suplimentare Aprobate': worker.approvedOvertimeHours > 0 ? worker.approvedOvertimeHours.toFixed(2) : '0',
      'Sambete Lucrate': worker.saturdayDaysWorked,
      'Ore Sambata': worker.saturdayTotalHoursWorkedFormatted,
      'Prima Zi Lucrata': worker.firstWorkDate,
      'Ultima Zi Lucrata': worker.lastWorkDate
    }));

    const wsSummary = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Sumar Luna');

    // Detailed work entries per worker (weekday only)
    report.workerReports.forEach(worker => {
      const workDetails = worker.workEntries.map(entry => ({
        'Data': entry.date,
        'Zi': entry.dayOfWeek,
        'Ora Start': entry.startTime,
        'Ora Sfarsit': entry.endTime,
        'Ore pe Santier': entry.hoursWorkedFormatted,
        'Pauza Masa (min)': entry.breakMinutes,
        'Ore Lucrate Fara Pauza': entry.effectiveHoursFormatted,
        'Ore Lucratoare (zi)': entry.expectedHoursPerDayFormatted,
        'Proiect': entry.projectName,
        'Locatie': entry.locationName,
        'Auto-inchis': entry.isAutoClosed ? 'Da' : 'Nu',
        'Comentarii': entry.comments
      }));

      const sheetName = `${worker.userName.substring(0, 25)}`.replace(/[\\/?*[\]]/g, '');
      const wsDetails = XLSX.utils.json_to_sheet(workDetails);
      XLSX.utils.book_append_sheet(wb, wsDetails, sheetName);
    });

    // Saturday summary sheet
    const saturdayData: any[] = [];
    report.workerReports.forEach(worker => {
      worker.saturdayWorkEntries.forEach(entry => {
        saturdayData.push({
          'Muncitor': worker.userName,
          'Echipa': worker.teamName,
          'Data': entry.date,
          'Ora Start': entry.startTime,
          'Ora Sfarsit': entry.endTime,
          'Ore pe Santier': entry.hoursWorkedFormatted,
          'Pauza Masa (min)': entry.breakMinutes,
          'Ore Lucrate Fara Pauza': entry.effectiveHoursFormatted,
          'Proiect': entry.projectName,
          'Locatie': entry.locationName
        });
      });
    });

    if (saturdayData.length > 0) {
      const wsSaturday = XLSX.utils.json_to_sheet(saturdayData);
      XLSX.utils.book_append_sheet(wb, wsSaturday, 'Sambete');
    }

    // Overtime details sheet
    const overtimeData: any[] = [];
    report.workerReports.forEach(worker => {
      worker.overtimeEntries.forEach(ot => {
        overtimeData.push({
          'Muncitor': worker.userName,
          'Data': ot.date,
          'Ore Suplimentare': ot.overtimeHoursFormatted,
          'Motiv': ot.reason,
          'Status': ot.isApproved ? 'Aprobat' : (ot.isDenied ? 'Respins' : 'In Asteptare'),
          'Auto-generat': ot.isAutoGenerated ? 'Da' : 'Nu',
          'Aprobat De': ot.approvedByName,
          'Data Aprobare': ot.approvedAt,
          'Comentariu Aprobare': ot.approvalComment
        });
      });
    });

    if (overtimeData.length > 0) {
      const wsOvertime = XLSX.utils.json_to_sheet(overtimeData);
      XLSX.utils.book_append_sheet(wb, wsOvertime, 'Overtime Detaliat');
    }

    XLSX.writeFile(wb, `Raport_HR_${report.monthName}_${report.year}.xlsx`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            Raport Lunar HR pentru Plată
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Raport detaliat lunar pentru fiecare muncitor cu ore lucrate, overtime, proiecte și locații.
              Sâmbetele sunt separate automat.
            </AlertDescription>
          </Alert>

          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[150px]">
              <label className="block text-sm font-medium mb-2">An</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="w-full px-3 py-2 border rounded-md"
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div className="flex-1 min-w-[150px]">
              <label className="block text-sm font-medium mb-2">Lună</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="w-full px-3 py-2 border rounded-md"
              >
                {months.map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>

            <Button
              onClick={handleGenerateReport}
              disabled={isLoading || parentLoading}
              className="flex items-center gap-2"
            >
              <Calendar className="h-4 w-4" />
              {isLoading ? 'Se Generează...' : 'Generează Raport'}
            </Button>

            {report && (
              <Button
                onClick={handleExportToExcel}
                variant="outline"
                className="flex items-center gap-2"
              >
                <FileSpreadsheet className="h-4 w-4" />
                Export Excel
              </Button>
            )}
          </div>

          {error && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Report Results */}
      {report && (
        <div className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Muncitori</p>
                    <p className="text-2xl font-bold">{report.totalWorkers}</p>
                  </div>
                  <Users className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Zile în Lună</p>
                    <p className="text-2xl font-bold">{report.totalDaysInMonth}</p>
                  </div>
                  <Calendar className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Ore Efectuate</p>
                    <p className="text-2xl font-bold">
                      {report.workerReports.reduce((sum, w) => sum + w.totalEffectiveHours, 0).toFixed(1)}h
                    </p>
                  </div>
                  <Clock className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Norma Îndeplinită</p>
                    <p className="text-2xl font-bold">
                      {report.workerReports.filter(w => w.metMonthlyQuota).length}/{report.totalWorkers}
                    </p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Worker Details */}
          <Card>
            <CardHeader>
              <CardTitle>Detalii Muncitori - {report.monthName} {report.year}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {report.workerReports.map(worker => {
                const isExpanded = expandedWorkers.has(worker.userId);
                return (
                  <Card key={worker.userId} className="border-2">
                    <CardContent className="pt-4">
                      {/* Worker Summary */}
                      <div
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => toggleWorkerExpansion(worker.userId)}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-lg">{worker.userName}</h3>
                            <Badge variant="secondary">{worker.workerType}</Badge>
                            <Badge variant="outline">{worker.teamName}</Badge>
                            <Badge
                              className={worker.metMonthlyQuota
                                ? 'bg-green-600 hover:bg-green-700 text-white'
                                : 'bg-red-600 hover:bg-red-700 text-white'}
                            >
                              {worker.metMonthlyQuota ? 'DA' : 'NU'} - Norma
                            </Badge>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                            <div>
                              <p className="text-gray-600">Zile Lucrate</p>
                              <p className="font-semibold">{worker.daysWorked} zile</p>
                            </div>
                            <div>
                              <p className="text-gray-600">Ore Efectuate</p>
                              <p className="font-semibold">{worker.totalEffectiveHoursFormatted}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">Ore Așteptate</p>
                              <p className="font-semibold">{worker.expectedHoursFormatted}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">Overtime Calculat</p>
                              <p className="font-semibold text-orange-600">
                                {worker.autoCalculatedOvertimeHoursFormatted}
                              </p>
                            </div>
                            {worker.saturdayDaysWorked > 0 && (
                              <div>
                                <p className="text-gray-600">Sâmbete</p>
                                <p className="font-semibold text-purple-600">
                                  {worker.saturdayDaysWorked} zile ({worker.saturdayTotalHoursWorkedFormatted})
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        <Button variant="ghost" size="sm">
                          {isExpanded ? <ChevronUp /> : <ChevronDown />}
                        </Button>
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="mt-4 pt-4 border-t space-y-4">
                          {/* Weekday Work Entries */}
                          <div>
                            <h4 className="font-semibold mb-2">Intrări de Lucru - Luni-Vineri ({worker.workEntries.length})</h4>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-3 py-2 text-left">Data</th>
                                    <th className="px-3 py-2 text-left">Zi</th>
                                    <th className="px-3 py-2 text-left">Start</th>
                                    <th className="px-3 py-2 text-left">Sfârșit</th>
                                    <th className="px-3 py-2 text-left">Ore pe Santier</th>
                                    <th className="px-3 py-2 text-left">Pauză</th>
                                    <th className="px-3 py-2 text-left">Ore Efectuate</th>
                                    <th className="px-3 py-2 text-left">Ore Așteptate</th>
                                    <th className="px-3 py-2 text-left">Proiect</th>
                                    <th className="px-3 py-2 text-left">Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {worker.workEntries.map((entry, idx) => (
                                    <tr key={idx} className="border-b">
                                      <td className="px-3 py-2">{entry.date}</td>
                                      <td className="px-3 py-2">{entry.dayOfWeek}</td>
                                      <td className="px-3 py-2">{entry.startTime}</td>
                                      <td className="px-3 py-2">{entry.endTime}</td>
                                      <td className="px-3 py-2">{entry.hoursWorkedFormatted}</td>
                                      <td className="px-3 py-2">{entry.breakMinutes} min</td>
                                      <td className="px-3 py-2 font-semibold">{entry.effectiveHoursFormatted}</td>
                                      <td className="px-3 py-2">{entry.expectedHoursPerDayFormatted}</td>
                                      <td className="px-3 py-2">{entry.projectName}</td>
                                      <td className="px-3 py-2">
                                        {entry.isAutoClosed && (
                                          <Badge variant="destructive" className="text-xs">Auto-închis</Badge>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Saturday Work Entries */}
                          {worker.saturdayWorkEntries.length > 0 && (
                            <div>
                              <h4 className="font-semibold mb-2 text-purple-700">
                                Sâmbete ({worker.saturdayWorkEntries.length}) - Total: {worker.saturdayTotalHoursWorkedFormatted}
                              </h4>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead className="bg-purple-50">
                                    <tr>
                                      <th className="px-3 py-2 text-left">Data</th>
                                      <th className="px-3 py-2 text-left">Start</th>
                                      <th className="px-3 py-2 text-left">Sfârșit</th>
                                      <th className="px-3 py-2 text-left">Ore pe Santier</th>
                                      <th className="px-3 py-2 text-left">Pauză</th>
                                      <th className="px-3 py-2 text-left">Ore Efectuate</th>
                                      <th className="px-3 py-2 text-left">Proiect</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {worker.saturdayWorkEntries.map((entry, idx) => (
                                      <tr key={idx} className="border-b bg-purple-50/30">
                                        <td className="px-3 py-2">{entry.date}</td>
                                        <td className="px-3 py-2">{entry.startTime}</td>
                                        <td className="px-3 py-2">{entry.endTime}</td>
                                        <td className="px-3 py-2">{entry.hoursWorkedFormatted}</td>
                                        <td className="px-3 py-2">{entry.breakMinutes} min</td>
                                        <td className="px-3 py-2 font-semibold">{entry.effectiveHoursFormatted}</td>
                                        <td className="px-3 py-2">{entry.projectName}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {/* Overtime Entries */}
                          {worker.overtimeEntries.length > 0 && (
                            <div>
                              <h4 className="font-semibold mb-2">Overtime ({worker.overtimeEntries.length})</h4>
                              <div className="space-y-2">
                                {worker.overtimeEntries.map((ot, idx) => (
                                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                                    <div>
                                      <p className="font-medium">{ot.date} - {ot.overtimeHoursFormatted}</p>
                                      <p className="text-sm text-gray-600">{ot.reason}</p>
                                    </div>
                                    <Badge
                                      variant={ot.isApproved ? "default" : (ot.isDenied ? "destructive" : "secondary")}
                                    >
                                      {ot.isApproved ? 'Aprobat' : (ot.isDenied ? 'Respins' : 'În Așteptare')}
                                    </Badge>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Summary Stats */}
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-4 bg-blue-50 rounded">
                            <div>
                              <p className="text-xs text-gray-600">Ore pe Santier</p>
                              <p className="font-semibold">{worker.totalHoursWorkedFormatted}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600">Ore Pauză</p>
                              <p className="font-semibold">{worker.totalBreakHoursFormatted}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600">Ore Efectuate</p>
                              <p className="font-semibold">{worker.totalEffectiveHoursFormatted}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600">Ore Așteptate</p>
                              <p className="font-semibold">{worker.expectedHoursFormatted}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600">Norma Completată</p>
                              <p className={`font-semibold ${worker.metMonthlyQuota ? 'text-green-600' : 'text-red-600'}`}>
                                {worker.metMonthlyQuota ? 'DA' : 'NU'}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
